import { debounce } from "lodash";
import {
  FC,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { SplitterStyled } from "@ryde/controls";
import {
  useCompanyScopeContext,
  useNewRideTemplate,
  useRidesNavigation,
  useRtl,
} from "@ryde/redux";
import {
  apiDataFetchingAtom,
  DateYMDString,
  GlobalAppEventEnum,
  mapOpenAF,
  RidesViewTypeEnum,
  useTimezone,
} from "@ryde/shared";

import { RidesRoutes } from "../../routes";
import {
  RIDES_CALENDAR_MAP_DEFAULT_GRID_PERCENT,
  RIDES_CALENDAR_MAP_MAX_GRID_PERCENT,
  RIDES_CALENDAR_MAP_MIN_GRID_PERCENT,
  RIDES_CALENDAR_MAP_RESIZE_THROTTLE_MS,
  RIDES_CALENDAR_OUTER_MIN_HEIGHT_PX,
  RIDES_CALENDAR_OUTER_VIEWPORT_OFFSET_PX,
  RidesCalendarContext,
  ridesCalendarDefaultContext,
  RidesCalendarMap,
  RidesCalendarWrapper,
} from "./parts";
import {
  RidesCalendarBodyStyled,
  RidesCalendarEmpty,
  RidesCalendarGridColumnStyled,
  RidesCalendarGridStyled,
  RidesCalendarPopoverGlobalStyles,
  RidesCalendarShellStyled,
  RidesCalendarTruncatedBanner,
} from "./parts/rides-calendar.styled";
import { slotStartIso } from "./rides-calendar.helpers";
import { useRidesCalendarEvents } from "./use-rides-calendar-events";

export const RidesCalendar: FC = (): ReactElement => {
  const { t } = useTranslation();
  const { events, truncated } = useRidesCalendarEvents();
  const { navigateRidesEdit, navigateRidesAdd } = useRidesNavigation();
  const { getNewRideTemplate } = useNewRideTemplate();
  const { timezone } = useTimezone();
  const { direction, isRtl } = useRtl();
  const { eventEmitter } = useCompanyScopeContext();
  const loadingData = useRecoilValue(apiDataFetchingAtom);
  const isMapOpen = useRecoilValue(mapOpenAF(RidesViewTypeEnum.Calendar));

  const [{ ridesCalendarHeight, ridesCalendarWidth }, setMetrics] = useState({
    ridesCalendarHeight: ridesCalendarDefaultContext.ridesCalendarHeight,
    ridesCalendarWidth: ridesCalendarDefaultContext.ridesCalendarWidth,
  });
  const [gridPercent, setGridPercent] = useState(
    RIDES_CALENDAR_MAP_DEFAULT_GRID_PERCENT,
  );
  const bodyRef = useRef<HTMLDivElement>(null);
  const gridColumnRef = useRef<HTMLDivElement>(null);

  const mapInvalidateSizeDebounced = useMemo(
    () =>
      debounce(
        () => eventEmitter?.emit(GlobalAppEventEnum.RESIZE_RIDE_MAP),
        RIDES_CALENDAR_MAP_RESIZE_THROTTLE_MS,
      ),
    [eventEmitter],
  );

  useEffect(() => {
    mapInvalidateSizeDebounced();
  }, [isMapOpen, mapInvalidateSizeDebounced]);

  const measureGrid = useCallback((): void => {
    const node = gridColumnRef.current;
    if (!node) {
      return;
    }
    const { width, height } = node.getBoundingClientRect();
    setMetrics({ ridesCalendarHeight: height, ridesCalendarWidth: width });
  }, []);

  useEffect(() => {
    measureGrid();
    window.addEventListener("resize", measureGrid);

    return () => {
      window.removeEventListener("resize", measureGrid);
    };
  }, [measureGrid, loadingData]);

  // The grid column also changes width when the map opens/closes or the
  // splitter is dragged — re-measure on those layout-affecting changes.
  useEffect(() => {
    measureGrid();
  }, [measureGrid, isMapOpen, gridPercent]);

  const handleDrag = (event: MouseEvent): void => {
    if (!bodyRef.current) {
      return;
    }
    const rect = bodyRef.current.getBoundingClientRect();
    const offsetX = isRtl
      ? rect.right - event.clientX
      : event.clientX - rect.left;
    const nextPercent = Math.max(
      RIDES_CALENDAR_MAP_MIN_GRID_PERCENT,
      Math.min(
        RIDES_CALENDAR_MAP_MAX_GRID_PERCENT,
        (offsetX / rect.width) * 100,
      ),
    );
    setGridPercent(nextPercent);
    mapInvalidateSizeDebounced();
  };

  const handleMouseUp = (): void => {
    document.removeEventListener("mousemove", handleDrag);
    document.removeEventListener("mouseup", handleMouseUp);
  };

  const handleMouseDown = (): void => {
    document.addEventListener("mousemove", handleDrag);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const contextValue = useMemo(
    () => ({ ridesCalendarHeight, ridesCalendarWidth }),
    [ridesCalendarHeight, ridesCalendarWidth],
  );

  const onOpenRide = (rideId: number): void => {
    navigateRidesEdit({ id: rideId });
  };

  const onAddRide = async (
    dateKey: DateYMDString,
    hour: number,
  ): Promise<void> => {
    const rideStart = slotStartIso({ dateKey, hour, timezone });
    const template = await getNewRideTemplate({ rideDate: rideStart });
    void navigateRidesAdd({
      keepQuery: true,
      initialData: template,
    });
  };

  return (
    <RidesCalendarContext.Provider value={contextValue}>
      <RidesCalendarPopoverGlobalStyles />
      {truncated && (
        <RidesCalendarTruncatedBanner data-e2e="rides-calendar-truncated">
          {t("ride.calendar.truncated", {
            defaultValue:
              "Showing a partial result — narrow the date range to see everything.",
          })}
        </RidesCalendarTruncatedBanner>
      )}
      <RidesCalendarShellStyled
        data-e2e="rides-calendar"
        style={{
          width: "100%",
          height: `calc(100vh - ${String(RIDES_CALENDAR_OUTER_VIEWPORT_OFFSET_PX)}px)`,
          minHeight: RIDES_CALENDAR_OUTER_MIN_HEIGHT_PX,
        }}
      >
        <RidesCalendarBodyStyled ref={bodyRef}>
          <RidesCalendarGridColumnStyled
            ref={gridColumnRef}
            $mapOpen={isMapOpen}
            $gridPercent={gridPercent}
          >
            <RidesCalendarGridStyled>
              {ridesCalendarHeight > 0 && ridesCalendarWidth > 0 && (
                <RidesCalendarWrapper
                  key={direction}
                  events={events}
                  onOpenRide={onOpenRide}
                  onAddRide={onAddRide}
                />
              )}
            </RidesCalendarGridStyled>
            {!events.length && !loadingData && (
              <RidesCalendarEmpty>
                {t("ride.calendar.empty", {
                  defaultValue: "No rides in the selected date range.",
                })}
              </RidesCalendarEmpty>
            )}
          </RidesCalendarGridColumnStyled>
          {isMapOpen && (
            <>
              <SplitterStyled onMouseDown={handleMouseDown} />
              <RidesCalendarMap />
            </>
          )}
        </RidesCalendarBodyStyled>
      </RidesCalendarShellStyled>
      <RidesRoutes />
    </RidesCalendarContext.Provider>
  );
};
